/**
 * HealthPass Card PDF Export Utility
 *
 * Generates printable card PDFs in multiple formats using jsPDF.
 * Client-side rendering, no server round-trip.
 */

import jsPDF from "jspdf";
import { walletItemLabel } from "../components/card/cardStyle";
import type { CardDTO, WalletItem } from "../services/api/cardService";
import { useI18nStore } from "../stores/i18nStore";
import { formatDate as formatLocalDate } from "./dateLocalization";

export type PaperFormat = "a4" | "a5" | "letter" | "card";

// Credit card ISO 7810 ID-1 dimensions in mm
const CARD_W = 85.6;
const CARD_H = 53.98;

/** A date in the app language: 10/01/2024 in English, 01.10.2024 in German. */
function formatDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  return formatLocalDate(iso, useI18nStore.getState().language) || "—";
}

/** "MM/YYYY" like a payment card's expiry — the same in every language. */
function formatMonthYear(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  const month = String(d.getMonth() + 1).padStart(2, "0");
  return `${month}/${d.getFullYear()}`;
}

/**
 * Main export entry point
 */
export async function exportCardAsPDF(
  cardData: CardDTO,
  walletItems: WalletItem[],
  format: PaperFormat = "a4",
  qrDataUrl: string | null = null,
): Promise<void> {
  if (format === "card") {
    await exportCreditCardFormat(cardData, qrDataUrl);
  } else {
    await exportPageFormat(cardData, walletItems, format, qrDataUrl);
  }
}

// ── Credit card format (85.6 × 54 mm, landscape) ─────────────────────────────

async function exportCreditCardFormat(
  cardData: CardDTO,
  qrDataUrl: string | null,
) {
  const doc = new jsPDF({
    orientation: "landscape",
    unit: "mm",
    format: [CARD_W, CARD_H],
  });

  await drawCardFront(doc, cardData, 0, 0, CARD_W, CARD_H, qrDataUrl);

  doc.save(`healthpass-card-${cardData.cardNumber}.pdf`);
}

// ── Page formats (A4 / A5 / Letter) ──────────────────────────────────────────

async function exportPageFormat(
  cardData: CardDTO,
  walletItems: WalletItem[],
  format: PaperFormat,
  qrDataUrl: string | null,
) {
  const pageFormats: Record<string, [number, number]> = {
    a4: [210, 297],
    a5: [148, 210],
    letter: [215.9, 279.4],
  };

  const pageDims = pageFormats[format] ?? pageFormats.a4;
  const [pageW, pageH] = pageDims!;
  const jsPdfFmt = (
    format === "letter" ? "letter" : format === "a5" ? "a5" : "a4"
  ) as "a4" | "a5" | "letter";
  const doc = new jsPDF({
    orientation: "portrait",
    unit: "mm",
    format: jsPdfFmt,
  });

  // Center the card on the page
  const x = (pageW - CARD_W) / 2;
  const y = 20;

  await drawCardFront(doc, cardData, x, y, CARD_W, CARD_H, qrDataUrl);

  // Wallet summary below card
  const walletY = y + CARD_H + 10;
  drawWalletSummary(doc, walletItems, x, walletY, CARD_W);

  // Footer
  doc.setFontSize(7);
  doc.setTextColor(150, 150, 150);
  doc.text("travelhealthpass.app", pageW / 2, pageH - 8, { align: "center" });

  doc.save(`healthpass-card-${cardData.cardNumber}.pdf`);
}

// ── Card front renderer ───────────────────────────────────────────────────────

async function drawCardFront(
  doc: jsPDF,
  cardData: CardDTO,
  x: number,
  y: number,
  w: number,
  h: number,
  qrDataUrl: string | null,
) {
  const r = 4; // corner radius

  // Background gradient – approximate with filled rect
  doc.setFillColor(29, 78, 216); // blue-700
  doc.roundedRect(x, y, w, h, r, r, "F");

  // Dark overlay strip (top)
  doc.setFillColor(30, 64, 175); // blue-800
  doc.rect(x, y, w, h * 0.35, "F");

  // Logo text
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(7);
  doc.setFont("helvetica", "bold");
  doc.text("HP", x + 4, y + 6);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(5.5);
  doc.text("HealthPass", x + 9, y + 6);

  // QR code (if available)
  if (qrDataUrl && qrDataUrl.startsWith("data:image")) {
    try {
      doc.addImage(qrDataUrl, "PNG", x + 3, y + 12, 16, 16);
    } catch {
      // QR not available – draw placeholder
      doc.setDrawColor(255, 255, 255);
      doc.setLineWidth(0.3);
      doc.rect(x + 3, y + 12, 16, 16);
      doc.setFontSize(4);
      doc.setTextColor(255, 255, 255);
      doc.text("QR", x + 8.5, y + 20, { align: "center" });
    }
  }

  // Avatar initials circle
  const avatarX = x + 24;
  const avatarY = y + 14;
  const avatarR = 7;
  doc.setFillColor(59, 130, 246); // blue-500
  doc.circle(avatarX + avatarR, avatarY + avatarR, avatarR, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(7);
  doc.setFont("helvetica", "bold");
  const initials =
    `${cardData.holder.firstName?.[0] ?? ""}${cardData.holder.lastName?.[0] ?? ""}`.toUpperCase();
  doc.text(initials, avatarX + avatarR, avatarY + avatarR + 2.5, {
    align: "center",
  });

  // Name & details
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.setTextColor(255, 255, 255);
  const nameText = `${cardData.holder.firstName} ${cardData.holder.lastName}`;
  doc.text(nameText, x + 40, y + 18, { maxWidth: w - 45 });

  doc.setFont("helvetica", "normal");
  doc.setFontSize(5.5);
  doc.setTextColor(180, 210, 255);
  const { t } = useI18nStore.getState();
  const genderIcon =
    cardData.holder.gender === "male"
      ? t.cardxPdfGenderMale
      : cardData.holder.gender === "female"
        ? t.cardxPdfGenderFemale
        : "?";
  doc.text(
    `${genderIcon}  ${formatDate(cardData.holder.dateOfBirth)}`,
    x + 40,
    y + 23,
  );
  doc.text(cardData.holder.nationality ?? "", x + 40, y + 27);

  // Divider line
  doc.setDrawColor(255, 255, 255, 0.3);
  doc.setLineWidth(0.2);
  doc.line(x + 3, y + h - 14, x + w - 3, y + h - 14);

  // Card number
  doc.setFont("courier", "bold");
  doc.setFontSize(7);
  doc.setTextColor(255, 255, 255);
  doc.text(cardData.cardNumber, x + 3, y + h - 9);

  // Valid until
  doc.setFont("helvetica", "normal");
  doc.setFontSize(5);
  doc.setTextColor(180, 210, 255);
  doc.text(t.cardxValidUntil, x + w - 3, y + h - 12, { align: "right" });
  doc.setFont("helvetica", "bold");
  doc.setFontSize(7);
  doc.setTextColor(255, 255, 255);
  doc.text(formatMonthYear(cardData.validUntil), x + w - 3, y + h - 8, {
    align: "right",
  });
}

// ── Wallet summary ────────────────────────────────────────────────────────────

function drawWalletSummary(
  doc: jsPDF,
  walletItems: WalletItem[],
  x: number,
  y: number,
  w: number,
) {
  doc.setFontSize(7);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(60, 60, 60);
  const { t } = useI18nStore.getState();
  doc.text(t.cardxPdfWalletHeading, x, y + 4);

  doc.setDrawColor(200, 200, 200);
  doc.setLineWidth(0.3);
  doc.line(x, y + 6, x + w, y + 6);

  const yOffset = y + 11;
  const colW = w / 2;

  walletItems.forEach((item, idx) => {
    const col = idx % 2;
    const row = Math.floor(idx / 2);
    const itemX = x + col * colW;
    const itemY = yOffset + row * 7;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(6.5);

    const statusText =
      item.status === "locked"
        ? `🔒 ${item.requiredTier}`
        : item.count !== null && item.count > 0
          ? `✓ ${item.count}`
          : item.status === "complete"
            ? "✓"
            : "—";

    doc.setTextColor(60, 60, 60);
    doc.text(`${item.icon}  ${walletItemLabel(item, t)}`, itemX, itemY);
    doc.setTextColor(
      item.status === "locked" ? 150 : item.status === "empty" ? 150 : 16,
      item.status === "locked" ? 150 : item.status === "empty" ? 150 : 120,
      item.status === "locked" ? 150 : item.status === "empty" ? 150 : 80,
    );
    doc.text(statusText, itemX + colW - 5, itemY, { align: "right" });
  });
}
